import { useMemo, useState } from 'react';
import { sparkToast } from '@spark';
import { addLabelToDataset, deleteEntityFromDataset } from '@/features/datasets/api/datasetApi';
import { buildNerAvailableTags } from '@/features/datasets/utils/nerAvailableTags';

/**
 * Shared "Available tags" catalog state and add/delete-tag flows for the NER
 * annotate modal — used by both the legacy `NERTagger` (NER with relations)
 * and the editable `NerAnnotateEditor` (plain NER).
 *
 * Owns:
 * - session-local newly-added / removed tag tracking (so a new tag keeps its
 *   creation position and a deleted tag disappears before `get-labels`
 *   refreshes),
 * - per-tag entity counts,
 * - the "+ New Tag" add flow (`add-entity`),
 * - the delete-tag confirmation state and `delete-entity` flow.
 *
 * `onCategoryDeleted(cat, result)` lets the caller sync its own entity state
 * after a category (and its annotations) was deleted server-side; when the
 * server returned an updated record it has already been pushed through
 * `updateRecord`.
 */
export default function useNerTagCatalog({
    entityOptions,
    entityLabels,
    llm_usecase_id,
    dataset,
    refreshCategories,
    fetchValidationCounts,
    onRefreshRecords,
    updateRecord,
    isBenchmark,
    recordId,
    onCategoryDeleted,
}) {
    // Tags created during this session, in creation order — kept out of the
    // alphabetical sort so a new tag stays put instead of jumping into place.
    const [newlyAddedTags, setNewlyAddedTags] = useState([]);
    // Tags deleted in this session. get-labels can still return them via
    // original_entity_names until that list is refreshed, so hide them locally.
    const [removedTags, setRemovedTags] = useState([]);
    // Confirmation dialog for deleting a tag from the "Available tags" legend.
    const [deleteVisible, setDeleteVisible] = useState(false);
    const [deleteTag, setDeleteTag] = useState('');

    const categoryCounts = useMemo(
        () =>
            (entityLabels || []).reduce((acc, label) => {
                acc[label] = (acc[label] || 0) + 1;
                return acc;
            }, {}),
        [entityLabels]
    );

    const allCategories = useMemo(
        () => buildNerAvailableTags(entityOptions, categoryCounts, newlyAddedTags, removedTags),
        [entityOptions, categoryCounts, newlyAddedTags, removedTags]
    );

    // Add a tag from the "Available tags" legend ("+ New Tag" inline editor).
    const addTag = (name) => {
        const trimmed = (name || '').trim();
        if (!trimmed || allCategories.includes(trimmed)) return;
        addLabelToDataset(
            llm_usecase_id,
            trimmed,
            () => {
                setNewlyAddedTags((prev) => (prev.includes(trimmed) ? prev : [...prev, trimmed]));
                setRemovedTags((prev) => prev.filter((tag) => tag !== trimmed));
                if (refreshCategories) refreshCategories();
            },
            () => {}
        );
    };

    const requestDeleteTag = (cat) => {
        setDeleteTag(cat);
        setDeleteVisible(true);
    };

    const confirmDeleteTag = () => {
        const cat = deleteTag;
        setDeleteVisible(false);
        deleteEntityFromDataset(
            llm_usecase_id,
            dataset,
            cat,
            (result) => {
                sparkToast.success(`Category "${cat}" and all associated entities deleted`);
                setRemovedTags((prev) => (prev.includes(cat) ? prev : [...prev, cat]));
                setNewlyAddedTags((prev) => prev.filter((tag) => tag !== cat));
                refreshCategories?.(cat);
                fetchValidationCounts?.();
                onRefreshRecords?.();
                if (result?.new_record?._id && updateRecord) {
                    updateRecord(result.new_record, isBenchmark);
                }
                onCategoryDeleted?.(cat, result);
            },
            () => {
                sparkToast.error(`Failed to delete category "${cat}"`);
            },
            true, // Always delete the annotations along with the category.
            recordId
        );
    };

    const deleteConfirm = {
        visible: deleteVisible,
        tag: deleteTag,
        onConfirm: confirmDeleteTag,
        onCancel: () => setDeleteVisible(false),
    };

    return { allCategories, categoryCounts, addTag, requestDeleteTag, deleteConfirm };
}
